import "./styles/App.css";
import { React, createContext, useState, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  CasRedirect,
  AdminAnnouncementsSearch,
  BillingSearch,
  Login,
  StudentSearch,
  CompanyAnnouncements,
  CompanySearch,
  UserProfile,
  EventSignup,
  Header,
  Footer,
  Homepage,
  JobSearch,
  AdminSearch,
  SkillsSearch,
  ResearchPositionSearch,
  FacultySearch,
  ResearchApplicantSearch,
  LocationsSearch,
  ResearchAreasSearch,
  Register,
  Reset,
  PasswordReset,
  Settings,
  Contacts,
  EventsList,
  EventSearch,
  ResearchDisplay,
  PublicJobDisplay
} from "./components"; // Import Settings component
import VerificationCode from "./components/VerificationCode";
import axios from "axios";
import { useCookies } from "react-cookie";
import { Navigate } from "react-router";
import Announcement from "./components/Company/Announcement";
import ProfilePicture from "./images/profile.png";

const production = true; //Set to true for production

if (production) {
  // revert back when finished
  //axios.defaults.baseURL = "https://vtjobboardbackendtest.discovery.cs.vt.edu";
  axios.defaults.baseURL = "https://jobhub.cs.vt.edu/api";
  axios.defaults.headers.post["Access-Control-Allow-Origin"] = "*";
  axios.defaults.headers.get["Access-Control-Allow-Origin"] = "*";
} else {
  axios.defaults.baseURL = "http://127.0.0.1:5000/api"; // for development
}

export const UserContext = createContext({});

function enclose(component) {
  return (
    <div className="Body flex flex-col h-full bg-brand-tertiary">
      <Header />
      {component}
      <Footer />
    </div>
  );
}

function App() {
  const [profilePicture, setProfilePicture] = useState(ProfilePicture);
  const [updatedProfilePicture, setUpdatedProfilePicture] = useState(false);

  const [cookies] = useCookies();
  const [dataLoaded, setDataLoaded] = useState(false);

  axios.interceptors.request.use(
    (config) => {
      const token = cookies.auth_token;
      if (token) {
        config.headers.authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  const [userType, setUserType] = useState("");
  const [username, setUsername] = useState("");
  useEffect(() => {
    axios
      .post("/loginDetails", { token: cookies.auth_token })
      .then((res) => {
        const data = res.data;
        if (data.success) {
          setUserType(data.user_type);
          setUsername(data.user);
        } else {
          setUserType("");
          setUsername("");
        }
      })
      .finally(() => {
        setDataLoaded(true);
      });
  }, [cookies.auth_token]);

  useEffect(() => {
    if (userType !== "student") {
      // only students have profile pictures, otherwise use default one
      setProfilePicture(ProfilePicture);
      return;
    }

    const body = new FormData();
    body.append("pid", username);
    axios
      .post("/student/getProfilePicture", body, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "image/png" });
        setProfilePicture(URL.createObjectURL(blob));
      });
  }, [updatedProfilePicture, username, userType]);

  if (!dataLoaded) {
    return;
  } else if (
    userType === "student" ||
    userType === "company" ||
    userType === "admin" ||
    userType === "faculty"
  ) {
    return (
      <UserContext.Provider
        value={{
          userType,
          setUserType,
          username,
          setUsername,
          profilePicture,
          updatedProfilePicture,
          setUpdatedProfilePicture,
        }}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={enclose(<Homepage />)} />
            {(userType === "student" ||
              userType === "admin" ||
              userType === "company") && (
              <Route path="/jobs" element={enclose(<JobSearch />)} />
            )}
            {(userType === "student" ||
              userType === "admin" ||
              userType === "faculty") && (
              <Route
                path="/researchpositions"
                element={enclose(<ResearchPositionSearch />)}
              />
            )}
            {userType === "student" && (
              <Route path="/profile" element={enclose(<UserProfile />)} />
            )}
            {userType !== "student" && userType !== "faculty" && (
              <Route path="/students" element={enclose(<StudentSearch />)} />
            )}
            {userType === "faculty" && (
              <Route
                path="/students"
                element={enclose(<ResearchApplicantSearch />)}
              />
            )}
            {userType === "admin" && (
              <Route path="/companies" element={enclose(<CompanySearch />)} />
            )}
            {userType === "admin" && (
              <Route path="/faculty" element={enclose(<FacultySearch />)} />
            )}
            {userType === "admin" && (
              <Route path="/skills" element={enclose(<SkillsSearch />)} />
            )}
            {userType === "admin" && (
              <Route path="/locations" element={enclose(<LocationsSearch />)} />
            )}
            {userType === "admin" && (
              <Route
                path="/researchareas"
                element={enclose(<ResearchAreasSearch />)}
              />
            )}
            {userType === "admin" && (
              <Route path="/admins" element={enclose(<AdminSearch />)} />
            )}
            {userType === "admin" && (
              <Route
                path="/adminAnnouncementsSearch"
                element={enclose(<AdminAnnouncementsSearch />)}
              />
            )}
            {userType === "admin" && (
              <Route path="/billing" element={enclose(<BillingSearch />)} />
            )}
            {userType === "admin" && (
              <Route path="/events" element={enclose(<EventSearch />)} />
            )}
            {userType === "company" && (
              <Route path="/announcement" element={enclose(<Announcement />)} />
            )}
            <Route path="/settings" element={enclose(<Settings />)} />{" "}
            {/* Add the settings route */}
            <Route path="/contacts" element={enclose(<Contacts />)} />
            <Route
              path="/companyannouncements"
              element={enclose(<CompanyAnnouncements />)}
            />
            <Route path="/publicresearchpositions" element={<ResearchDisplay />} />
            <Route path="/publicjobs" element={<PublicJobDisplay />} />
            <Route path="/eventsignup" element={enclose(<EventSignup />)} />
            <Route path="/eventslist" element={enclose(<EventsList />)} />
            <Route path="*" element={enclose(<Navigate to="/" />)} />
          </Routes>
        </BrowserRouter>
      </UserContext.Provider>
    );
  } else {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={enclose(<Login />)} />
          <Route path="/cas-redirect" element={<CasRedirect />} />
          <Route path="/register" element={enclose(<Register />)} />
          <Route path="/reset-password" element={enclose(<Reset />)} />
          <Route
            path="/verification-code"
            element={enclose(<VerificationCode />)}
          />
          <Route path="/password-reset" element={enclose(<PasswordReset />)} />
          <Route path="/login" element={enclose(<Login />)} />
          <Route path="/publicresearchpositions" element={<ResearchDisplay />} />
          <Route path="/publicjobs" element={<PublicJobDisplay />} />
          <Route path="*" element={enclose(<Navigate to="/" />)} />
        </Routes>
      </BrowserRouter>
    );
  }
}

export default App;
